import { useState } from 'react'
import toast from 'react-hot-toast'
import { AlertTriangle, CheckCircle2, Database, Play, RefreshCw } from 'lucide-react'
import { useMigrationStatus, useRunMigrations } from '../../hooks/useMigrationStatus'

function confirmMigrationRun(label: string) {
  return window.confirm(
    `${label}\n\nDie Datenbankstruktur wird dabei geändert. Fortfahren?`
  )
}

export default function MigrationStatusPanel() {
  const { data: status, isLoading, isError, refetch, isFetching } = useMigrationStatus({
    refetchInterval: false
  })
  const runMigrations = useRunMigrations()
  const [lastRunMessage, setLastRunMessage] = useState<string | null>(null)

  const handleRun = async (migrations: string | string[], label: string) => {
    if (!confirmMigrationRun(label)) {
      return
    }

    setLastRunMessage(null)

    try {
      const result = await runMigrations.mutateAsync(migrations)
      setLastRunMessage(result.message || null)

      if (result.success) {
        toast.success(result.message || 'Migration(en) erfolgreich ausgeführt.')
      } else {
        const failed = result.data.results.find((entry) => !entry.success)
        toast.error(failed?.error || result.message || 'Migration fehlgeschlagen.')
      }
    } catch (error: unknown) {
      const message = error instanceof Error
        ? error.message
        : 'Migration konnte nicht ausgeführt werden.'
      toast.error(message)
    }
  }

  if (isLoading) {
    return (
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Migrationsstatus wird geladen...
        </div>
      </div>
    )
  }

  if (isError || !status) {
    return (
      <div className="border border-red-200 rounded-lg bg-red-50 p-6">
        <p className="text-sm font-medium text-red-800">Migrationsstatus konnte nicht geladen werden.</p>
        <button
          type="button"
          onClick={() => void refetch()}
          className="mt-3 inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm text-red-700 hover:bg-red-100"
        >
          <RefreshCw className="h-4 w-4" />
          Erneut prüfen
        </button>
      </div>
    )
  }

  const isHealthy = status.isUpToDate
  const isRunning = runMigrations.isLoading
  const nextMigration = status.pendingMigrations[0]

  return (
    <div className={`rounded-lg border p-6 ${isHealthy ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${isHealthy ? 'bg-emerald-100' : 'bg-amber-100'}`}>
            {isHealthy ? (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <Database className="h-5 w-5 text-gray-500" />
              Datenbank-Updates
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {isHealthy
                ? 'Die Datenbank ist auf dem aktuellen Stand.'
                : status.message}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {status.pendingCount > 0 && (
            <>
              <button
                type="button"
                disabled={isRunning || !nextMigration}
                onClick={() => void handleRun(
                  nextMigration.version,
                  `Update ${nextMigration.version} (${nextMigration.name}) ausführen?`
                )}
                className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
              >
                <Play className="h-4 w-4" />
                Nächstes Update ausführen
              </button>
              <button
                type="button"
                disabled={isRunning}
                onClick={() => void handleRun(
                  'all',
                  `Alle ${status.pendingCount} ausstehenden Updates ausführen?`
                )}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-60"
              >
                <Play className="h-4 w-4" />
                Alle ausführen
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching || isRunning}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching || isRunning ? 'animate-spin' : ''}`} />
            Aktualisieren
          </button>
        </div>
      </div>

      {lastRunMessage && (
        <p className="mt-4 rounded-md border border-white/70 bg-white/80 px-3 py-2 text-sm text-gray-700">
          {lastRunMessage}
        </p>
      )}

      <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-md border border-white/70 bg-white/70 px-3 py-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Ausgeführt</dt>
          <dd className="mt-1 text-lg font-semibold text-gray-900">{status.executedCount}</dd>
        </div>
        <div className="rounded-md border border-white/70 bg-white/70 px-3 py-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Ausstehend</dt>
          <dd className={`mt-1 text-lg font-semibold ${status.pendingCount > 0 ? 'text-amber-700' : 'text-gray-900'}`}>
            {status.pendingCount}
          </dd>
        </div>
        <div className="rounded-md border border-white/70 bg-white/70 px-3 py-2">
          <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">Letzte Migration</dt>
          <dd className="mt-1 truncate text-sm font-medium text-gray-900" title={status.latestExecuted || undefined}>
            {status.latestExecuted || '—'}
          </dd>
        </div>
      </dl>

      {!status.trackingAvailable && (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-amber-800">
            Die Tabelle <code className="rounded bg-amber-100 px-1 py-0.5 text-xs">migrations</code> existiert noch nicht.
          </p>
          <button
            type="button"
            disabled={isRunning}
            onClick={() => void handleRun('005', 'Basis-Migration 005 ausführen?')}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
          >
            <Play className="h-4 w-4" />
            Basis-Migration starten
          </button>
        </div>
      )}

      {status.pendingCount > 0 && (
        <div className="mt-5">
          <h4 className="text-sm font-semibold text-gray-900">Fehlende Updates</h4>
          <div className="mt-3 overflow-hidden rounded-lg border border-amber-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Nr.</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Datei</th>
                  <th className="px-4 py-2 text-left font-medium text-gray-500">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {status.pendingMigrations.map((migration) => (
                  <tr key={migration.filename}>
                    <td className="px-4 py-2 font-medium text-gray-900">{migration.version}</td>
                    <td className="px-4 py-2 text-gray-700">
                      <div className="font-medium">{migration.name}</div>
                      <div className="text-xs text-gray-500">{migration.filename}</div>
                    </td>
                    <td className="px-4 py-2">
                      <button
                        type="button"
                        disabled={isRunning}
                        onClick={() => void handleRun(
                          migration.version,
                          `Update ${migration.version} (${migration.name}) ausführen?`
                        )}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                      >
                        <Play className="h-3.5 w-3.5" />
                        Ausführen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            Nur für Administratoren. Bei Fehlern stoppt die Ausführung; bereits erfolgreiche Schritte bleiben bestehen.
          </p>
        </div>
      )}
    </div>
  )
}
